import React, { createContext, useCallback, useEffect, useRef, useState } from 'react';
import ActionCable from 'actioncable';
import { getToken } from '@/services/auth';
import { baseURL } from '@/services/api';

export const LiveChallengeContext = createContext();

export const LiveChallengeProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [timerSeconds, setTimerSeconds] = useState(30);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [myScore, setMyScore] = useState(null);
  const [participantCount, setParticipantCount] = useState(0);
  const [participants, setParticipants] = useState([]);
  const [answerCount, setAnswerCount] = useState({ answered: 0, total: 0 });
  const [sessionStatus, setSessionStatus] = useState(null);
  const [questionResult, setQuestionResult] = useState(null);
  const [hasAnswered, setHasAnswered] = useState(false);
  const [lastAnswerFeedback, setLastAnswerFeedback] = useState(null);
  // True após broadcast question_closed; false após reveal de próxima Q ou reset.
  // Desabilita botões de resposta no aluno pra evitar respostas stale que chegariam
  // ao backend depois do current_question_index ter avançado.
  const [isQuestionClosed, setIsQuestionClosed] = useState(false);
  // True entre o reveal e o close da questão atual. Distinto de currentQuestion:
  // após F5 do admin, o serializer retorna current_question (Qn+1 carregada do DB)
  // mesmo antes de revelar; questionRevealed só vira true quando o Redis tem o
  // timestamp gravado em SessionControlService#reveal_question.
  const [questionRevealed, setQuestionRevealed] = useState(false);
  // Sinaliza para as telas do aluno (WaitingRoom/QuestionPage/AnswerFeedback)
  // que o admin reiniciou a sessão e elas devem navegar de volta ao /entrar.
  // É consumido uma vez e limpado via clearSessionReset.
  const [sessionReset, setSessionReset] = useState(false);
  const [isConnected, setIsConnected] = useState(false);

  const cableRef = useRef(null);
  const subscriptionsRef = useRef([]);
  const timerRef = useRef(null);
  // subscribe está em useCallback([], deps=[]) — o connected() callback captura
  // o snapshot do primeiro render, então currentQuestion/session via closure
  // ficam stale. Refs servem para ler o valor atual sem refazer subscribe a cada
  // mudança de state (o que invalidaria a subscription do ActionCable).
  const currentQuestionIdRef = useRef(null);
  const sessionRef = useRef(null);
  // Timeout do retry de resync (erros transientes no show REST). Mantido em ref
  // para que o unsubscribe possa cancelar pendentes — evita aplicar state em
  // componente desmontado ou em sessão já encerrada.
  const resyncTimeoutRef = useRef(null);
  // Timestamp do último subscribe() — usado pra distinguir "fui removido do desafio"
  // de "replica lag pós-join". Logo após o POST /join, um show contra read replica
  // que ainda não replicou o INSERT retorna current_user_participant=false; sem
  // grace window o aluno era jogado de volta a /entrar imediatamente após escanear
  // o QR.
  const subscribeTimestampRef = useRef(null);
  // Generation counter incrementada a cada subscribe(). Cada doResync captura a
  // generation no início do request e descarta a resposta se a generation atual
  // for outra — protege contra duas races:
  //   (a) aluno troca de challenge: show(A) em flight resolve depois do subscribe(B);
  //   (b) WS broadcast question_revealed chega antes do show() responder, e a
  //       resposta REST traz snapshot stale.
  // Substitui o race guard anterior baseado em startQuestionId, que tinha bypass
  // no mount inicial (startQuestionId=null sempre passava o teste).
  const subscribeGenerationRef = useRef(0);
  // Challenge atualmente assinado (id+role). Permite que páginas student (Question/
  // Waiting/AnswerFeedback) chamem `subscribe` no mount como rede de segurança para
  // F5 — sem o guard, chamar `subscribe` repetidamente faria reset destrutivo do
  // state. Resseta no unsubscribe.
  const subscribedToRef = useRef(null);
  // Janela após subscribe em que current_user_participant=false / 404 são ignorados
  // (assumidos como replica lag). Valor conservador — em Heroku/RDS típica < 1s.
  const REPLICA_LAG_GRACE_MS = 5000;

  useEffect(() => {
    currentQuestionIdRef.current = currentQuestion?.id ?? null;
  }, [currentQuestion?.id]);

  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  // A participação do aluno morreu no backend (reset de lobby destruiu o
  // participant, ou o resync detectou a remoção). Sinaliza as telas para navegar
  // ao /entrar (sessionReset) e invalida o guard idempotente: o re-join
  // subsequente ao MESMO desafio é um join NOVO (participant recriado), não uma
  // duplicata — precisa rodar o subscribe completo (limpa sessionReset stale,
  // renova a grace window de replica lag, dispara doResync). Sem a invalidação,
  // o session_reset (student stream) que chega após o clearSessionReset da tela
  // deixava a flag stale e o aluno tinha que digitar o código de acesso 2x.
  const endStudentParticipation = useCallback(() => {
    subscribedToRef.current = null;
    setSessionReset(true);
  }, []);

  const subscribe = useCallback((liveChallengeId, role) => {
    // Idempotente: páginas student chamam subscribe no mount como fallback para
    // F5 (sem isso, refresh em QuestionPage/WaitingRoom não reconecta WS porque
    // JoinPage é o único caller no fluxo feliz). Se já estamos assinados ao mesmo
    // challenge+role, no-op — sem isso, cada mount duplicaria o reset destrutivo
    // de state abaixo, piscando a UI pro placeholder e perdendo flags do questionResult.
    if (subscribedToRef.current === `${liveChallengeId}:${role}` && subscriptionsRef.current.length > 0) {
      return;
    }
    subscribedToRef.current = `${liveChallengeId}:${role}`;

    // Ao trocar de desafio (aluno fez join em outro), limpa estado e
    // subscriptions anteriores. Sem isso, sessionStatus='in_progress' do
    // desafio antigo permanece e WaitingRoom navega errado.
    subscriptionsRef.current.forEach(sub => sub?.unsubscribe());
    subscriptionsRef.current = [];
    if (cableRef.current) {
      cableRef.current.disconnect();
      cableRef.current = null;
    }

    setSessionReset(false);
    setCurrentQuestion(null);
    setQuestionIndex(0);
    setTotalQuestions(0);
    setHasAnswered(false);
    setIsQuestionClosed(false);
    setQuestionRevealed(false);
    setLastAnswerFeedback(null);
    setQuestionResult(null);
    setSessionStatus(null);
    setLeaderboard([]);
    setMyScore(null);
    setParticipantCount(0);
    setParticipants([]);
    setAnswerCount({ answered: 0, total: 0 });
    subscribeTimestampRef.current = Date.now();
    subscribeGenerationRef.current += 1;

    const token = getToken();
    const apiUrl = baseURL();
    const cableUrl = apiUrl.replace(/^http(s?):\/\/(.*?)\/api\/v1\/?$/, (_, s, host) => `ws${s}://${host}/cable`) + `?token=${encodeURIComponent(token)}`;

    cableRef.current = ActionCable.createConsumer(cableUrl);

    const channelParams = {
      channel: 'LiveChallengeChannel',
      live_challenge_id: liveChallengeId
    };

    // Resync REST após reconect/F5. Encapsulado em função local para permitir
    // retry em erros transientes (5xx, ECONNABORTED, ERR_NETWORK) sem capturar
    // closures stale — cada attempt recaptura generation do ref atual.
    const doResync = attempt => {
      if (resyncTimeoutRef.current) {
        clearTimeout(resyncTimeoutRef.current);
        resyncTimeoutRef.current = null;
      }
      // Generation snapshot: se a generation atual no ref mudar até o response
      // chegar, significa que outro subscribe() rodou (aluno trocou de challenge)
      // OU outro doResync foi iniciado (reconnect storm). Em ambos casos a
      // resposta é potencialmente stale e deve ser descartada — esta é a única
      // proteção contra race entre subscribe(novoChallenge) e show(challengeAntigo)
      // em flight.
      const startGeneration = subscribeGenerationRef.current;

      import('@/services/liveChallenges').then(({ liveChallengesApi }) => {
        liveChallengesApi
          .show(liveChallengeId)
          .then(response => {
            if (subscribeGenerationRef.current !== startGeneration) {
              return;
            }

            const attrs = response?.data?.attributes;
            if (!attrs) return;

            setSessionStatus(attrs.status);
            setParticipantCount(attrs['participants-count'] ?? 0);

            // Cenário 13 (desafio público): broadcast session_reset perdido por tela
            // bloqueada. show retorna 200 com status=lobby — indistinguível do estado
            // pré-reset, exceto pelo current-user-participant=false. Em desafio privado
            // a resposta é 404 (visible_to filtra por participant) — tratado no catch.
            // Grace window evita falso-positivo por replica lag: imediatamente após o
            // POST /join, um show contra replica não replicada retorna participant=false.
            if (role === 'student' && sessionRef.current?.participant_id && attrs['current-user-participant'] === false) {
              const sinceSubscribe = Date.now() - (subscribeTimestampRef.current ?? 0);
              if (sinceSubscribe > REPLICA_LAG_GRACE_MS) {
                endStudentParticipation();
              } else {
                // Ainda dentro da grace window: pode ser replica lag transient OU
                // remoção real. Re-checar logo após a janela expirar — sem isso, o
                // aluno legítimamente removido pós-F5 ficava preso até o próximo
                // reconnect do WS (que pode demorar minutos).
                const delay = REPLICA_LAG_GRACE_MS - sinceSubscribe + 100;
                resyncTimeoutRef.current = setTimeout(() => doResync(attempt), delay);
              }
              return;
            }

            // Resync da questão ativa: se backend diz que está in_progress e
            // tem current-question, restaura currentQuestion + timer com o
            // tempo restante real (computado no servidor a partir do Redis).
            if (attrs.status === 'in_progress' && attrs['current-question']) {
              const remaining = attrs['time-remaining-seconds'] ?? 0;
              const revealed = attrs['question-revealed'] ?? false;
              const newQuestion = attrs['current-question'];
              const previousAnswer = attrs['participant-answer-for-current-question'];
              const lastResult = attrs['last-question-result'];

              // Cenário 14: aluno offline durante reveal next. Quando a Q mudou,
              // os flags da Q anterior (hasAnswered/feedback/result/isClosed) precisam
              // resetar — caso contrário, o aluno volta na questão certa mas com state
              // stale (botões disabled, mensagem "resposta enviada", etc.).
              if (newQuestion?.id && newQuestion.id !== currentQuestionIdRef.current) {
                setHasAnswered(false);
                setIsQuestionClosed(false);
                setLastAnswerFeedback(null);
                setQuestionResult(null);
              }

              setCurrentQuestion(newQuestion);
              // Update síncrono do ref: a useEffect que mirrora currentQuestion?.id
              // só roda após o commit do render. Em StrictMode dev (double-invoke) ou
              // reconnect storm, um segundo doResync pode rodar antes do commit e ler
              // o ref antigo, disparando o reset de flags acima desnecessariamente.
              currentQuestionIdRef.current = newQuestion?.id ?? null;
              setQuestionIndex(attrs['current-question-index'] ?? 0);
              setTotalQuestions(attrs['total-questions'] ?? 0);
              setTimerSeconds(attrs['timer-seconds']);
              setQuestionRevealed(revealed);
              if (revealed && remaining > 0) {
                startTimer(remaining);
              } else {
                // Questão ainda não revelada (F5 do admin entre reveals) OU
                // revelada mas expirada (esperando broadcast question_closed).
                // NÃO setamos isQuestionClosed aqui: o backend nos diz isso de
                // forma explícita via last-question-result (próximo bloco) quando
                // a Q encerrou de fato; assumir close prematuro causava regressão
                // no Cenário 15 (aluno tardio via QR via 'Questão encerrada').
                setTimeRemaining(0);
                setIsTimerRunning(false);
              }

              // Cenário "respondi e perdi state no F5": backend devolve a resposta
              // persistida do aluno na current_question. Restaura hasAnswered +
              // feedback para o aluno ver "Resposta enviada!" em vez de placeholder.
              if (previousAnswer) {
                setHasAnswered(true);
                setLastAnswerFeedback({
                  is_correct: previousAnswer.is_correct,
                  points_earned: previousAnswer.points_earned,
                  correct_alternative_id: previousAnswer.live_challenge_alternative_id
                });
              }

              // Cenário "broadcast question_closed perdido": backend devolve o
              // resultado da última Q encerrada (correct + distribution + leaderboard).
              // Populamos questionResult — o useEffect das telas (aluno/admin) navega
              // pra /resultado naturalmente, evitando limbo permanente.
              if (lastResult) {
                // Estamos exibindo o resultado da questão ENCERRADA (a próxima ainda
                // não foi revelada — last_question_result é gateado por
                // !next_question_revealed no backend). O broadcast question_revealed
                // da questão encerrada trazia question_index = current_question_index-1;
                // alinhar aqui evita que isLastQuestion (idx+1>=total, usado no aluno e
                // no botão Próxima/Finalizar do admin) marque "conclusão" prematuramente
                // no gap antes da última questão. Sobrescreve o setQuestionIndex acima
                // (que usa o índice da PRÓXIMA a revelar, correto só pra questão ativa).
                setQuestionIndex(Math.max(0, (attrs['current-question-index'] ?? 0) - 1));
                setIsQuestionClosed(true);
                setQuestionResult({
                  correctAlternativeId: lastResult.correct_alternative_id,
                  answerDistribution: lastResult.answer_distribution,
                  leaderboard: lastResult.leaderboard
                });
                if (lastResult.leaderboard) {
                  setLeaderboard(lastResult.leaderboard);
                }
              }
            } else if (attrs.status === 'in_progress' && !attrs['current-question']) {
              // Todas as questões já foram encerradas — o admin está na tela de
              // "Finalizar desafio" (status permanece in_progress até o clique em
              // Finalizar). Sem este branch, o lastResult ficava preso atrás do
              // gate de current_question acima e o ControlPanel pós-F5 ficava em
              // phase=questionActive renderizando QuestionView com a última Q,
              // levando o admin a clicar "Revelar questão" → 500 RecordNotFound.
              const lastResult = attrs['last-question-result'];
              if (lastResult) {
                setQuestionIndex(attrs['current-question-index'] ?? 0);
                setTotalQuestions(attrs['total-questions'] ?? 0);
                setIsQuestionClosed(true);
                setQuestionResult({
                  correctAlternativeId: lastResult.correct_alternative_id,
                  answerDistribution: lastResult.answer_distribution,
                  leaderboard: lastResult.leaderboard
                });
                if (lastResult.leaderboard) {
                  setLeaderboard(lastResult.leaderboard);
                }
              }
            }
          })
          .catch(err => {
            // Mesmo race guard do branch 200: outra subscribe() invalida esta resposta.
            if (subscribeGenerationRef.current !== startGeneration) {
              return;
            }
            // Cenário 13 (desafio privado): visible_to filtra por participant; quando
            // o aluno é destruído por back_to_lobby, o show responde 404. Tratamos
            // como reset para que o aluno navegue de volta a /entrar. Mesmo grace
            // window do branch 200 — replica lag pós-join também produz 404 transient.
            if (role === 'student' && sessionRef.current?.participant_id && err?.response?.status === 404) {
              const sinceSubscribe404 = Date.now() - (subscribeTimestampRef.current ?? 0);
              if (sinceSubscribe404 > REPLICA_LAG_GRACE_MS) {
                endStudentParticipation();
              } else {
                const delay = REPLICA_LAG_GRACE_MS - sinceSubscribe404 + 100;
                resyncTimeoutRef.current = setTimeout(() => doResync(attempt), delay);
              }
              return;
            }
            // Erros transientes (5xx, network, timeout): WS continua subscrito
            // mas o broadcast que disparou o reconnect já foi consumido. Sem retry
            // o aluno fica congelado no placeholder até o próximo broadcast.
            // `!err.response` cobre network failure puro onde axios não seta status
            // nem code (alguns adapters em jsdom/CORS preflight reject).
            const status = err?.response?.status;
            const code = err?.code;
            const isTransient = code === 'ECONNABORTED' || code === 'ERR_NETWORK' || !err?.response || (status >= 500 && status < 600);
            if (isTransient && attempt < 1) {
              resyncTimeoutRef.current = setTimeout(() => doResync(attempt + 1), 3000);
            }
          });
      });
    };

    const subscription = cableRef.current.subscriptions.create(channelParams, {
      received: message => handleMessage(message, role),
      connected: () => {
        setIsConnected(true);
        // Re-sync via REST após reconectar/F5: pega status + question_index +
        // tempo restante real. Sem isso o broadcast question_revealed (que só
        // é emitido 1x no reveal) é perdido e a UI fica com timer zerado ou
        // resetado pro valor inicial do timer_seconds.
        doResync(0);
      },
      disconnected: () => setIsConnected(false)
    });

    subscriptionsRef.current.push(subscription);
  }, []);

  const unsubscribe = useCallback(() => {
    subscriptionsRef.current.forEach(sub => {
      if (sub) sub.unsubscribe();
    });
    subscriptionsRef.current = [];
    subscribedToRef.current = null;

    if (resyncTimeoutRef.current) {
      clearTimeout(resyncTimeoutRef.current);
      resyncTimeoutRef.current = null;
    }

    if (cableRef.current) {
      cableRef.current.disconnect();
      cableRef.current = null;
    }

    stopTimer();
  }, []);

  const handleMessage = useCallback(
    (message, role) => {
      const { type, data } = message;

      switch (type) {
        case 'participant_joined':
          // Não incrementamos count aqui — o broadcast `participant_count` paralelo
          // (vindo do Redis incr no channel) já é a fonte canônica. Incrementar aqui
          // causava dupla-contagem (um aluno entrava e virava 2 no painel do admin).
          setParticipants(prev => {
            const key = data?.user_id ?? data?.id ?? data?.name;
            if (key && prev.some(p => (p.user_id ?? p.id ?? p.name) === key)) return prev;
            return [...prev, data];
          });
          break;

        case 'participant_count':
          setParticipantCount(data.count);
          break;

        case 'participant_left':
          // Aluno fechou aba / perdeu conexão (unsubscribed disparou no channel).
          // Remove da lista de presence imediatamente — o broadcast paralelo de
          // participant_count atualiza o contador.
          setParticipants(prev => prev.filter(p => (p.user_id ?? p.id) !== data.user_id));
          break;

        case 'participants_snapshot':
          // Emitido no subscribed do teacher pra hidratar a lista de presence ao
          // abrir/F5 do painel admin — substitui o array em vez de mesclar.
          setParticipants(Array.isArray(data.participants) ? data.participants : []);
          break;

        case 'back_to_lobby':
        case 'session_reset':
          // Admin reiniciou a sessão. Sem este handler o sessionStatus permanecia
          // 'in_progress' e o useEffect do ControlPanel snap-ava o phase de volta
          // para 'questionActive', fazendo a tela parecer que nada aconteceu.
          setSessionStatus('lobby');
          setCurrentQuestion(null);
          setQuestionResult(null);
          setQuestionIndex(0);
          setLeaderboard([]);
          setMyScore(null);
          setHasAnswered(false);
          setQuestionRevealed(false);
          setLastAnswerFeedback(null);
          setAnswerCount({ answered: 0, total: 0 });
          setParticipantCount(0);
          setParticipants([]);
          stopTimer();
          // Para alunos: marca o flag para que a tela atual (Aguardando/Questão/
          // Resposta) navegue de volta ao /entrar — o participante foi destruído
          // no backend, então não faz sentido continuar na sessão. Também invalida
          // o guard do subscribe pro re-join valer como join novo (ver comentário
          // de endStudentParticipation).
          if (role === 'student') endStudentParticipation();
          break;

        case 'answer_count_update':
          setAnswerCount({ answered: data.answered, total: data.total });
          break;

        case 'question_revealed':
          setCurrentQuestion(data.question);
          setQuestionIndex(data.question_index);
          setTotalQuestions(data.total_questions);
          setTimerSeconds(data.timer_seconds);
          setTimeRemaining(data.timer_seconds);
          setIsTimerRunning(true);
          setHasAnswered(false);
          setIsQuestionClosed(false);
          setQuestionRevealed(true);
          setLastAnswerFeedback(null);
          setQuestionResult(null);
          // Updater function lê o valor atual via React internals — sem isso, o
          // ActionCable received() arrow captura permanentemente o handleMessage
          // do primeiro render (subscribe é useCallback([])), e answerCount.total
          // ficava congelado em 0 → admin via 'X de 0 participantes' a partir da Q2.
          setAnswerCount(prev => ({ answered: 0, total: prev.total }));
          startTimer(data.timer_seconds);
          break;

        case 'question_closed':
          setIsTimerRunning(false);
          setIsQuestionClosed(true);
          setQuestionRevealed(false);
          stopTimer();
          setQuestionResult({
            correctAlternativeId: data.correct_alternative_id,
            answerDistribution: data.answer_distribution,
            leaderboard: data.leaderboard
          });
          if (data.leaderboard) {
            setLeaderboard(data.leaderboard);
          }
          break;

        case 'leaderboard_update':
          setLeaderboard(data);
          break;

        case 'session_started':
          setSessionStatus('in_progress');
          break;

        case 'session_finished':
          setSessionStatus('finished');
          setIsTimerRunning(false);
          stopTimer();
          if (data.leaderboard) {
            setLeaderboard(data.leaderboard);
          }
          break;

        default:
          break;
      }
    },
    // Sem dep de state — todos setStates dentro usam updater functions ou values
    // do payload (data.*). Manter [] garante identidade estável e impede o
    // ActionCable received() arrow de capturar uma closure stale.
    []
  );

  const timerEndRef = useRef(null);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    timerEndRef.current = null;

    setIsTimerRunning(false);
  }, []);

  const startTimer = useCallback(
    seconds => {
      stopTimer();

      const endAt = Date.now() + seconds * 1000;
      timerEndRef.current = endAt;

      setTimeRemaining(seconds);
      setIsTimerRunning(true);

      timerRef.current = setInterval(() => {
        const remaining = Math.max(0, Math.ceil((timerEndRef.current - Date.now()) / 1000));

        setTimeRemaining(remaining);

        if (remaining <= 0) {
          stopTimer();
        }
      }, 250);
    },
    [stopTimer]
  );
  useEffect(() => {
    return () => {
      unsubscribe();
      stopTimer();
    };
  }, []);

  const resetState = useCallback(() => {
    setSession(null);
    setCurrentQuestion(null);
    setQuestionIndex(0);
    setTotalQuestions(0);
    setLeaderboard([]);
    setMyScore(null);
    setParticipantCount(0);
    setParticipants([]);
    setAnswerCount({ answered: 0, total: 0 });
    setSessionStatus(null);
    setQuestionResult(null);
    setHasAnswered(false);
    setIsQuestionClosed(false);
    setQuestionRevealed(false);
    setLastAnswerFeedback(null);
    setSessionReset(false);
  }, []);

  const clearSessionReset = useCallback(() => setSessionReset(false), []);

  useEffect(() => {
    const handleVisibility = () => {
      if (!document.hidden && isTimerRunning && timerEndRef.current) {
        const remaining = Math.max(0, Math.ceil((timerEndRef.current - Date.now()) / 1000));

        setTimeRemaining(remaining);

        if (remaining <= 0) {
          stopTimer();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [isTimerRunning, stopTimer]);

  const value = {
    session,
    setSession,
    currentQuestion,
    questionIndex,
    totalQuestions,
    timerSeconds,
    timeRemaining,
    isTimerRunning,
    leaderboard,
    setLeaderboard,
    myScore,
    setMyScore,
    participantCount,
    participants,
    answerCount,
    sessionStatus,
    setSessionStatus,
    questionResult,
    hasAnswered,
    setHasAnswered,
    isQuestionClosed,
    questionRevealed,
    lastAnswerFeedback,
    setLastAnswerFeedback,
    sessionReset,
    isConnected,
    clearSessionReset,
    subscribe,
    unsubscribe,
    resetState,
    startTimer,
    stopTimer
  };

  return <LiveChallengeContext.Provider value={value}>{children}</LiveChallengeContext.Provider>;
};
